/**
 * Classify a PDF page as real digital text vs a scanned (image) page using
 * text-quality signals from getTextContent() and image-dominance signals from
 * getOperatorList(). No vision call is needed.
 */

export type PdfPageClass = "text" | "scanned";

export type TextContentItem = {
  str?: string;
  hasEOL?: boolean;
};

export type OperatorListLike = {
  fnArray?: Array<number | string>;
};

/** pdf.js OPS names we care about (string form; numeric OPS also accepted). */
const IMAGE_OPS = new Set([
  "paintImageXObject",
  "paintInlineImageXObject",
  "paintJpegXObject",
  // Numeric OPS values from pdf.js (stable across recent versions)
  85, // paintImageXObject
  86, // paintInlineImageXObject
  87, // paintInlineImageXObjectGroup
  88, // paintImageMaskXObject
  89, // paintImageMaskXObjectGroup
  90, // paintImageMaskXObjectRepeat
  91, // paintImageXObjectRepeat
  92, // paintSolidColorImageMask
  93, // paintXObject
]);

const TEXT_OPS = new Set([
  "showText",
  "showSpacedText",
  "nextLineShowText",
  "nextLineSetSpacingShowText",
  // Numeric OPS
  44, // showText
  45, // showSpacedText
  46, // nextLineShowText
  47, // nextLineSetSpacingShowText
]);

export type TextQuality = {
  rawChars: number;
  wordCount: number;
  avgItemLen: number;
  itemCount: number;
};

export function measureTextQuality(items: TextContentItem[]): TextQuality {
  const strs = items
    .map((item) => (typeof item.str === "string" ? item.str : ""))
    .filter((s) => s.length > 0);
  const joined = strs.join("");
  const rawChars = joined.replace(/\s/g, "").length;
  const wordMatches = joined.match(/[A-Za-z]{3,}|[0-9]{2,}/g) || [];
  const wordCount = wordMatches.length;
  const itemCount = strs.length;
  const avgItemLen =
    itemCount === 0
      ? 0
      : strs.reduce((sum, s) => sum + s.length, 0) / itemCount;
  return { rawChars, wordCount, avgItemLen, itemCount };
}

export function isUsableText(quality: TextQuality): boolean {
  return (
    quality.rawChars >= 80 && quality.wordCount >= 8 && quality.avgItemLen >= 3
  );
}

/**
 * A text layer rich enough that the page is certainly digital text, however
 * many image operators it paints. Many PDFs draw glyphs, rules or logos as
 * hundreds of tiny image masks, which would otherwise make `isImageHeavy`
 * flag a full page of real text as scanned.
 */
export function isRichText(quality: TextQuality): boolean {
  return (
    quality.rawChars >= 250 &&
    quality.wordCount >= 30 &&
    quality.avgItemLen >= 3
  );
}

export type OperatorCounts = {
  imageOps: number;
  textOps: number;
  totalOps: number;
};

export function countOperatorSignals(
  operatorList: OperatorListLike | null | undefined
): OperatorCounts {
  const fnArray = operatorList?.fnArray || [];
  let imageOps = 0;
  let textOps = 0;
  fnArray.forEach((fn) => {
    if (IMAGE_OPS.has(fn as never)) imageOps += 1;
    else if (TEXT_OPS.has(fn as never)) textOps += 1;
  });
  return { imageOps, textOps, totalOps: fnArray.length };
}

export function isImageHeavy(counts: OperatorCounts): boolean {
  if (counts.imageOps < 1) return false;
  return counts.imageOps >= counts.textOps || counts.textOps === 0;
}

/**
 * A page with no usable text and no image ops (and almost no ops at all) is
 * treated as blank. Blank pages still count as "scanned" in pageStats so
 * scannedPages + textPages === totalPages, but the vision call is skipped.
 */
export function isBlankPage(
  quality: TextQuality,
  counts: OperatorCounts
): boolean {
  return (
    !isUsableText(quality) &&
    counts.imageOps === 0 &&
    counts.totalOps < 8 &&
    quality.rawChars < 20
  );
}

/**
 * Classify a page. Returns "text" when the text layer is rich, or when it is
 * usable AND the page is not image-heavy. Everything else is "scanned"
 * (including blanks).
 */
export function classifyPdfPage(
  textItems: TextContentItem[],
  operatorList?: OperatorListLike | null
): PdfPageClass {
  const quality = measureTextQuality(textItems);
  if (isRichText(quality)) return "text";
  const counts = countOperatorSignals(operatorList);
  if (isUsableText(quality) && !isImageHeavy(counts)) {
    return "text";
  }
  return "scanned";
}
